/**
 * SignaturePadField — wraps the `signature_pad` library for drawn signature
 * capture. Used by the receipt-sign panel in `RecordPatientPaymentModal`
 * (legacy parity with `document_receipt_signature_viewer.php` jSignature pads).
 *
 * Exposes a ref-based imperative `getDataUrl()` so the parent can read the
 * current canvas state at submit time.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import SignaturePad from 'signature_pad';
import { Button } from '@/components/ui/Button';

export interface SignaturePadFieldHandle {
  getDataUrl: () => string | null;
  clear: () => void;
  isEmpty: () => boolean;
}

export interface SignaturePadFieldProps {
  label: string;
  height?: number;
}

export const SignaturePadField = forwardRef<SignaturePadFieldHandle, SignaturePadFieldProps>(
  function SignaturePadField({ label, height = 140 }, ref) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const padRef = useRef<SignaturePad | null>(null);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const resize = () => {
        const ratio = window.devicePixelRatio || 1;
        canvas.width = canvas.offsetWidth * ratio;
        canvas.height = canvas.offsetHeight * ratio;
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.scale(ratio, ratio);
        padRef.current?.clear();
      };

      padRef.current = new SignaturePad(canvas, {
        backgroundColor: 'rgba(255,255,255,1)',
        penColor: '#000',
      });
      resize();
      window.addEventListener('resize', resize);

      return () => {
        window.removeEventListener('resize', resize);
        padRef.current?.off();
        padRef.current = null;
      };
    }, []);

    useImperativeHandle(ref, () => ({
      getDataUrl: () => {
        const pad = padRef.current;
        if (!pad || pad.isEmpty()) return null;
        return pad.toDataURL('image/png');
      },
      clear: () => padRef.current?.clear(),
      isEmpty: () => padRef.current?.isEmpty() ?? true,
    }));

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-slate-700">{label}</span>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => padRef.current?.clear()}
          >
            Clear
          </Button>
        </div>
        <canvas
          ref={canvasRef}
          style={{ height: `${height}px` }}
          className="w-full rounded border border-slate-300 bg-white"
        />
      </div>
    );
  },
);
